import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ListPlus, Search, ScanLine, ListChecks, CalendarCheck, X } from 'lucide-react';
import { HANDOUT_STATUS_LABELS, type HandoutContextDto, type HandoutOrderDto, type HandoutResultDto, type HandoutTodayDto } from '@thebb/shared';
import { get, post } from '../../api';
import { useAuth } from '../../auth';
import { Camera } from '../../scanner/Camera';
import { beepError, beepScan, beepSuccess } from '../../scanner/feedback';
import { TopBar, Toast, errText } from './Mobile';

type Tab = 'scan' | 'search' | 'list' | 'today';
type ListItem = { id: string; number: number; name: string; error?: string };
const LIST_KEY = 'handout.list';
const SHOP_KEY = 'handout.shop';

const tone: Record<string, string> = { pending: 'bg-amber-100 text-amber-900', handed_out: 'bg-success-100 text-success', approval_required: 'bg-blue-100 text-blue-900', cancelled: 'bg-zinc-200 text-zinc-700' };
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const readList = (): ListItem[] => {
  try {
    return JSON.parse(localStorage.getItem(LIST_KEY) ?? '[]') as ListItem[];
  } catch {
    return [];
  }
};

/**
 * HO-01: QR2's scanner on the handheld. Scan the QR on the employee's receipt (or search by name),
 * hand the gift out, take it back within the recall window, collect several in a list, and see
 * today's own activity.
 */
export function MobileHandout() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const ctx = useQuery({ queryKey: ['handout-context'], queryFn: () => get<HandoutContextDto>('/handout/context') });
  const [shopId, setShopId] = useState(() => localStorage.getItem(SHOP_KEY) ?? '');
  const [tab, setTab] = useState<Tab>('scan');
  const [sheet, setSheet] = useState<HandoutOrderDto | null>(null);
  const [msg, setMsg] = useState<{ text: string; tone: 'ok' | 'err' } | null>(null);
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [list, setList] = useState<ListItem[]>(readList);
  const last = useRef<{ code: string; at: number } | null>(null);

  useEffect(() => {
    if (ctx.data && (!shopId || !ctx.data.shops.some((s) => s.id === shopId))) setShopId(ctx.data.shopId ?? '');
  }, [ctx.data, shopId]);
  useEffect(() => {
    try {
      if (shopId) localStorage.setItem(SHOP_KEY, shopId);
      localStorage.setItem(LIST_KEY, JSON.stringify(list));
    } catch {
      /* private mode */
    }
  }, [shopId, list]);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const show = (text: string, t: 'ok' | 'err') => {
    setMsg({ text, tone: t });
    window.setTimeout(() => setMsg((m) => (m?.text === text ? null : m)), 4000);
  };

  const onDetect = async (raw: string) => {
    const now = Date.now();
    if (last.current && last.current.code === raw && now - last.current.at < 3000) return; // the same code again within 3 s
    last.current = { code: raw, at: now };
    beepScan();
    try {
      const o = await get<HandoutOrderDto>(`/handout/lookup?code=${encodeURIComponent(raw)}${shopId ? `&shopId=${shopId}` : ''}`);
      setSheet(o);
    } catch (e) {
      beepError();
      show(errText(e), 'err');
    }
  };

  const afterResult = (r: HandoutResultDto) => {
    setSheet(r.order);
    void qc.invalidateQueries({ queryKey: ['handout-today'] });
    if (r.result === 'fulfilled' || r.result === 'recalled') {
      beepSuccess();
      show(`${r.order.number} · ${r.message}`, 'ok');
    } else if (r.result === 'already_fulfilled') show(r.message, 'ok');
    else {
      beepError();
      show(r.message, 'err');
    }
  };

  const addToList = (o: HandoutOrderDto) => {
    if (!list.some((x) => x.id === o.id)) setList([...list, { id: o.id, number: o.number, name: o.name }]);
    setSheet(null);
    show(`${o.number} er på listen`, 'ok');
  };

  const shops = ctx.data?.shops ?? [];
  return (
    <>
      <TopBar
        title="Udlevering"
        back={can('floor.pick')}
        right={
          shops.length > 1 ? (
            <select className="max-w-40 truncate rounded-lg bg-white/15 px-2 py-1.5 text-sm font-semibold text-white" value={shopId} onChange={(e) => setShopId(e.target.value)}>
              {shops.map((s) => <option key={s.id} value={s.id} className="text-ink">{s.name}</option>)}
            </select>
          ) : (
            <span className="truncate text-sm opacity-80">{shops[0]?.name ?? ''}</span>
          )
        }
      />
      {!online && <div className="bg-danger px-3 py-1.5 text-center text-sm font-semibold text-white">Offline: udlevering er slået fra, indtil forbindelsen er tilbage</div>}
      {ctx.data && !shops.length && <Toast msg="Du er ikke knyttet til en shop. Kontakt din administrator." tone="err" />}
      {ctx.data?.scope && <div className="bg-blue-50 px-3 py-1 text-center text-xs text-blue-900">Dit område: {ctx.data.scope.kind === 'department' ? ctx.data.scope.departments.join(', ') : ctx.data.scope.kind === 'zip' ? `postnr. ${ctx.data.scope.from}-${ctx.data.scope.to}` : 'alle'}</div>}
      {msg && <Toast msg={msg.text} tone={msg.tone} />}

      <div className="flex-1 pb-20">
        {tab === 'scan' && <Camera active={!sheet} hint="Scan QR-koden på kvitteringen" onDetect={(c) => void onDetect(c)} />}
        {tab === 'search' && <SearchTab shopId={shopId} onOpen={setSheet} />}
        {tab === 'list' && <ListTab list={list} setList={setList} online={online} onDone={(n) => { beepSuccess(); show(`${n} udleveret`, 'ok'); void qc.invalidateQueries({ queryKey: ['handout-today'] }); }} />}
        {tab === 'today' && <TodayTab canRecall={can('handout.recall')} online={online} onResult={afterResult} />}
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-20 grid grid-cols-4 border-t border-line bg-white" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {([['scan', 'Scan', ScanLine], ['search', 'Søg', Search], ['list', `Liste${list.length ? ` (${list.length})` : ''}`, ListChecks], ['today', 'I dag', CalendarCheck]] as const).map(([k, label, Icon]) => (
          <button key={k} className={`flex flex-col items-center gap-0.5 py-2 text-xs font-semibold ${tab === k ? 'text-primary' : 'text-muted'}`} onClick={() => setTab(k)}>
            <Icon size={22} /> {label}
          </button>
        ))}
      </nav>

      {sheet && <ResultSheet order={sheet} online={online} canRecall={can('handout.recall')} onClose={() => setSheet(null)} onResult={afterResult} onList={addToList} onError={(e) => { beepError(); show(errText(e), 'err'); }} />}
    </>
  );
}

function ResultSheet({ order: o, online, canRecall, onClose, onResult, onList, onError }: { order: HandoutOrderDto; online: boolean; canRecall: boolean; onClose: () => void; onResult: (r: HandoutResultDto) => void; onList: (o: HandoutOrderDto) => void; onError: (e: unknown) => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmRecall, setConfirmRecall] = useState(false);
  const [zoom, setZoom] = useState<string | null>(null);
  const act = async (kind: 'handout' | 'recall') => {
    setBusy(true);
    try {
      onResult(await post<HandoutResultDto>(`/handout/orders/${o.id}/${kind}`, { note: note || undefined, app: 'scan' }));
      setNote('');
      setConfirmRecall(false);
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-30 flex items-end bg-black/40" onClick={onClose}>
      <div className="max-h-[90dvh] w-full overflow-y-auto rounded-t-3xl bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()} style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <span className={`inline-block rounded-full px-3 py-1 text-sm font-bold ${tone[o.status]}`}>{HANDOUT_STATUS_LABELS[o.status]}</span>
            <div className="mt-2 text-xl font-bold">{o.name || '(uden navn)'}</div>
            <div className="text-sm text-muted">Ordre {o.number}{o.zip ? ` · ${o.zip}` : ''}{o.department ? ` · ${o.department}` : ''}</div>
            {o.email && <div className="text-xs text-muted">{o.email}</div>}
          </div>
          <button className="rounded-full p-2 text-muted active:bg-canvas" onClick={onClose} aria-label="Luk"><X size={22} /></button>
        </div>
        <div className="space-y-2">
          {o.products.map((p, i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl bg-canvas p-2">
              {p.image ? <button onClick={() => setZoom(p.image)}><img src={p.image} alt="" className="h-16 w-16 rounded-lg bg-white object-contain" /></button> : <div className="h-16 w-16 rounded-lg bg-white" />}
              <div className="text-base font-semibold">{p.quantity > 1 ? `${p.quantity} × ` : ''}{p.title}</div>
            </div>
          ))}
        </div>
        {o.status === 'handed_out' && (
          <div className="mt-3 rounded-xl bg-success-100 p-3 text-sm text-success">
            <CheckCircle2 className="mr-1 inline" size={16} /> Udleveret {fmt(o.handedOutAt)} af {o.handedOutByName}
            {o.note && <div className="mt-1 italic">"{o.note}"</div>}
            {o.recallUntil && <div className="mt-1 text-xs">Kan tages tilbage til {new Date(o.recallUntil).toLocaleDateString('da-DK')}</div>}
          </div>
        )}
        {online && (o.status === 'pending' || (o.status === 'handed_out' && canRecall && o.canRecall)) && (
          <input className="mt-3 w-full rounded-xl border border-line px-3 py-3 text-base" placeholder="Note (valgfri)" value={note} onChange={(e) => setNote(e.target.value)} />
        )}
        <div className="mt-3 grid gap-2">
          {online && o.status === 'pending' && (
            <>
              <button className="rounded-2xl bg-primary py-4 text-lg font-bold text-white active:opacity-90 disabled:opacity-50" disabled={busy} onClick={() => void act('handout')}>Udlevér</button>
              <button className="flex items-center justify-center gap-2 rounded-2xl bg-canvas py-3 font-semibold active:bg-line" onClick={() => onList(o)}><ListPlus size={18} /> Tilføj til liste</button>
            </>
          )}
          {online && o.status === 'handed_out' && canRecall && o.canRecall && (
            confirmRecall ? (
              <div className="grid grid-cols-2 gap-2">
                <button className="rounded-2xl bg-danger py-3 font-bold text-white disabled:opacity-50" disabled={busy} onClick={() => void act('recall')}>Ja, tag tilbage</button>
                <button className="rounded-2xl bg-canvas py-3 font-semibold" onClick={() => setConfirmRecall(false)}>Nej</button>
              </div>
            ) : (
              <button className="rounded-2xl bg-canvas py-3 font-semibold text-danger active:bg-line" onClick={() => setConfirmRecall(true)}>Tag tilbage</button>
            )
          )}
        </div>
      </div>
      {zoom && <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/80 p-6" onClick={(e) => { e.stopPropagation(); setZoom(null); }}><img src={zoom} alt="" className="max-h-full max-w-full rounded-xl bg-white object-contain" /></div>}
    </div>
  );
}

function SearchTab({ shopId, onOpen }: { shopId: string; onOpen: (o: HandoutOrderDto) => void }) {
  const [q, setQ] = useState('');
  const results = useQuery({ queryKey: ['handout-search', shopId, q], queryFn: () => get<HandoutOrderDto[]>(`/handout/search?shopId=${shopId}&q=${encodeURIComponent(q.trim())}`), enabled: !!shopId && q.trim().length >= 2 });
  return (
    <div className="space-y-3 p-3">
      <input className="w-full rounded-2xl border border-line bg-white px-4 py-3 text-base" placeholder="Navn, e-mail, afdeling eller ordrenr." autoFocus value={q} onChange={(e) => setQ(e.target.value)} />
      <ul className="divide-y divide-line overflow-hidden rounded-2xl bg-white shadow-card">
        {results.data?.map((o) => (
          <li key={o.id}>
            <button className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-primary-50" onClick={() => onOpen(o)}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{o.name}</div>
                <div className="truncate text-xs text-muted">{o.number} · {o.department ?? ''} · {o.products.map((p) => p.title).join(', ')}</div>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone[o.status]}`}>{HANDOUT_STATUS_LABELS[o.status]}</span>
            </button>
          </li>
        ))}
        {q.trim().length >= 2 && results.data?.length === 0 && <li className="px-4 py-3 text-sm text-muted">Ingen fundet</li>}
      </ul>
    </div>
  );
}

function ListTab({ list, setList, online, onDone }: { list: ListItem[]; setList: (l: ListItem[]) => void; online: boolean; onDone: (n: number) => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await post<{ results: { orderId: string; result: string; message: string }[] }>('/handout/bulk', { orderIds: list.slice(0, 50).map((x) => x.id), note: note || undefined });
      const ok = new Set(r.results.filter((x) => x.result === 'fulfilled' || x.result === 'already_fulfilled').map((x) => x.orderId));
      setList(list.filter((x) => !ok.has(x.id)).map((x) => ({ ...x, error: r.results.find((y) => y.orderId === x.id)?.message })));
      onDone(r.results.filter((x) => x.result === 'fulfilled').length);
    } finally {
      setBusy(false);
    }
  };
  if (!list.length) return <p className="p-6 text-center text-sm text-muted">Listen er tom. Scan en gave og tryk "Tilføj til liste" for at udlevere flere på én gang.</p>;
  return (
    <div className="space-y-3 p-3">
      <ul className="divide-y divide-line overflow-hidden rounded-2xl bg-white shadow-card">
        {list.map((x) => (
          <li key={x.id} className="flex items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1"><div className="truncate font-semibold">{x.name}</div><div className="text-xs text-muted">{x.number}</div>{x.error && <div className="text-xs text-danger">{x.error}</div>}</div>
            <button className="p-2 text-muted" onClick={() => setList(list.filter((y) => y.id !== x.id))} aria-label="Fjern"><X size={18} /></button>
          </li>
        ))}
      </ul>
      <input className="w-full rounded-xl border border-line bg-white px-3 py-3" placeholder="Fælles note (valgfri)" value={note} onChange={(e) => setNote(e.target.value)} />
      <button className="w-full rounded-2xl bg-primary py-4 text-lg font-bold text-white disabled:opacity-50" disabled={busy || !online} onClick={() => void run()}>Udlevér alle ({Math.min(list.length, 50)})</button>
      <button className="w-full py-2 text-sm text-muted" onClick={() => setList([])}>Tøm listen</button>
    </div>
  );
}

function TodayTab({ canRecall, online, onResult }: { canRecall: boolean; online: boolean; onResult: (r: HandoutResultDto) => void }) {
  const today = useQuery({ queryKey: ['handout-today'], queryFn: () => get<HandoutTodayDto>('/handout/today'), refetchInterval: 30_000 });
  const label: Record<string, string> = { handout: 'Udleveret', recall: 'Taget tilbage', handout_refused: 'Afvist', recall_refused: 'Ikke taget tilbage' };
  // "Fortryd" on the newest hand-out per order, if it has not been taken back since.
  const undoable = new Set<string>();
  const seen = new Set<string>();
  for (const e of today.data?.events ?? []) {
    if (seen.has(e.orderId)) continue;
    seen.add(e.orderId);
    if (e.action === 'handout') undoable.add(e.id);
  }
  const undo = async (orderId: string) => onResult(await post<HandoutResultDto>(`/handout/orders/${orderId}/recall`, { note: 'Fortrudt', app: 'scan' }));
  return (
    <div className="space-y-3 p-3">
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-2xl bg-white p-3 shadow-card"><div className="text-2xl font-bold">{today.data?.handouts ?? 0}</div><div className="text-xs text-muted">udleveret</div></div>
        <div className="rounded-2xl bg-white p-3 shadow-card"><div className="text-2xl font-bold">{today.data?.recalls ?? 0}</div><div className="text-xs text-muted">taget tilbage</div></div>
        <div className="rounded-2xl bg-white p-3 shadow-card"><div className="text-2xl font-bold">{today.data?.refused ?? 0}</div><div className="text-xs text-muted">afvist</div></div>
      </div>
      <ul className="divide-y divide-line overflow-hidden rounded-2xl bg-white shadow-card">
        {today.data?.events.map((e) => (
          <li key={e.id} className="flex items-center gap-3 px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{e.name ?? ''} · {e.orderNumber}</div>
              <div className="text-xs text-muted">{new Date(e.at).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })} · {label[e.action] ?? e.action}{e.note ? ` · ${e.note}` : ''}</div>
            </div>
            {canRecall && online && undoable.has(e.id) && <button className="rounded-lg bg-canvas px-3 py-1.5 text-xs font-semibold" onClick={() => void undo(e.orderId)}>Fortryd</button>}
          </li>
        ))}
        {today.data?.events.length === 0 && <li className="px-4 py-3 text-sm text-muted">Intet i dag endnu.</li>}
      </ul>
    </div>
  );
}
